// Check-ins (architecture sprint-04 §5.5, F48; fixes INV #8). Client-only: slots come from the
// server-owned anchor and the server-projected now, acknowledgements live per browser in
// localStorage + BroadcastChannel. No server write.
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useMe } from '@/hooks/use-me';
import { useTimer } from '@/hooks/use-timer';
import { checkinState, isAcknowledged } from '@/lib/checkin';
import { showCheckinNotification } from '@/lib/notifications';
import { broadcast, subscribe } from '@/lib/sync';
import { serverNowMs } from '@/lib/timer-time';
import { useNowTick } from '@/lib/use-now';
import type { TimerDto } from '@/types/timer';
import { useTimerActions } from './TimerActionsProvider';

export const CHECKIN_ACK_KEY = 'tt-checkin-ack';
const TICK_MS = 1000;

function checkinLabel(timer: TimerDto): string {
  return timer.description ?? timer.project?.name ?? 'this timer';
}

export function CheckinController(): JSX.Element {
  const { data: snapshot } = useTimer();
  const { data: me } = useMe();
  const actions = useTimerActions();
  const perfNow = useNowTick(TICK_MS);
  const [openSlot, setOpenSlot] = useState<string | null>(null);
  const memoryAck = useRef<string | null>(null);
  const lastShown = useRef<string | null>(null);

  const active = Boolean(snapshot && snapshot.timer.pausedAt === null && me);

  function readAck(): string | null {
    try {
      return window.localStorage.getItem(CHECKIN_ACK_KEY) ?? memoryAck.current;
    } catch {
      return memoryAck.current;
    }
  }

  function writeAck(slotId: string): void {
    memoryAck.current = slotId;
    try {
      window.localStorage.setItem(CHECKIN_ACK_KEY, slotId);
    } catch {
      // Storage blocked: the in-memory ack covers this tab.
    }
  }

  useEffect(() => {
    // Sprint 11 (F10): the resolved rules of the timer's project decide on/off and the interval (an
    // enforced project value beats the person's preference). Off → no slot ever opens.
    const rules = snapshot?.timer.rules;
    if (!active || !snapshot || !me || rules?.checkinEnabled === false) {
      if (openSlot !== null) setOpenSlot(null);
      return;
    }
    const state = checkinState({
      anchorMs: Date.parse(snapshot.timer.checkinAnchorAt),
      intervalMinutes: rules?.checkinIntervalMinutes ?? me.preferences.checkinIntervalMinutes,
      serverNowMs: serverNowMs(snapshot, perfNow),
    });
    const { slotId } = state;
    if (!slotId || slotId === lastShown.current || isAcknowledged(slotId, readAck())) return;
    lastShown.current = slotId;
    // An open, unanswered modal advances to the newest slot instead of stacking a second one.
    setOpenSlot(slotId);
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden' && me.preferences.notificationsEnabled) {
      showCheckinNotification(slotId, checkinLabel(snapshot.timer));
    }
    // perfNow drives the re-check every tick; readAck reads refs and storage only.
  }, [active, snapshot, me, perfNow, openSlot]);

  useEffect(
    () =>
      subscribe((msg) => {
        if (msg.type !== 'checkin-answered') return;
        memoryAck.current = msg.slotId;
        setOpenSlot((current) => (current !== null && isAcknowledged(current, msg.slotId) ? null : current));
      }),
    [],
  );

  function answer(switched: boolean): void {
    if (openSlot !== null) {
      writeAck(openSlot);
      broadcast({ type: 'checkin-answered', slotId: openSlot });
    }
    setOpenSlot(null);
    if (switched) actions.openSwitch();
  }

  const label = snapshot ? checkinLabel(snapshot.timer) : 'this timer';
  return (
    <Dialog
      open={openSlot !== null}
      onOpenChange={(open) => {
        // Esc, the backdrop and the close button all mean "Yes".
        if (!open) answer(false);
      }}
    >
      {/* The original's small confirm: Yes first (autofocused), then I switched. */}
      <DialogContent className="max-w-sm" showClose={false}>
        <DialogHeader>
          <DialogTitle>Still working?</DialogTitle>
          <DialogDescription>
            Still on <span className="font-medium text-foreground">{label}</span>?
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Button type="button" autoFocus className="flex-1" onClick={() => answer(false)}>
            Yes, still on this
          </Button>
          {snapshot?.timer.rules?.switchAllowed !== false && (
            <Button type="button" variant="outline" className="flex-1" onClick={() => answer(true)}>
              I switched
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
